<template>
  <div
    :class="componentClasses"
  >
    <Icon
      v-if="iconBefore"
      :class="iconClasses"
      class="stk-control-icon stk-control-icon-before"
      :icon="iconBefore"
    />

    <!-- Textarea -->
    <textarea
      v-if="type === 'textarea'"
      :id="id"
      ref="inputRef"
      :key="'ta'"
      :class="inputClasses"
      :value="inputValue || ''"
      :disabled="disabled"
      :maxlength="maxlength"
      :name="name"
      :placeholder="placeholder"
      :readonly="readonly"
      :required="required"
      @input="onInput"
      @blur="onBlur"
      @focus="onFocus"
    />

    <!-- Input -->
    <input
      v-else
      :id="id"
      ref="inputRef"
      :key="'in-'+type"
      :class="inputClasses"
      :value="inputValue || ''"
      :disabled="disabled"
      :maxlength="maxlength"
      :name="name"
      :placeholder="placeholder"
      :readonly="readonly"
      :type="type"
      :required="required"
      @input="onInput"
      @blur="onBlur"
      @focus="onFocus"
    >

    <div
      v-if="loading"
      :class="loadingClasses"
    >
      <Loader />
    </div>
  </div>
</template>

<script setup>
import { defineEmits, defineExpose, computed, ref } from 'vue';
import Icon from '../../graphic/icon/Icon.vue';
import Loader from '../../graphic/loader/Loader.vue';

const props = defineProps({
  value: String, // Vue 2
  modelValue: String, // Vue 3
  iconBefore: {
    type: String,
  },
  type: {
    type: String,
    default: 'text',
    required: false,
  },
  disabled: {
    type: Boolean,
    default: false,
    required: false,
  },
  id: {
    type: String,
    default: '',
    required: false,
  },
  maxlength: {
    type: Number,
    default: null,
    required: false,
  },
  name: {
    type: String,
    default: '',
    required: false,
  },
  placeholder: {
    type: String,
    default: '',
    required: false,
  },
  readonly: {
    type: Boolean,
    default: false,
    required: false,
  },
  required: {
    type: Boolean,
    default: false,
    required: false,
  },
  loading: {
    type: Boolean,
    default: false,
    required: false,
  },
  size: {
    type: String,
    default: '',
    required: false,
  },
  spacing: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['input', 'update:modelValue']);

const inputRef = ref(null);

defineExpose({
  focus: () => inputRef.value?.focus(),
  blur: () => inputRef.value?.blur(),
});

const onInput = e => {
  emit('input', e.target.value); // Vue 2
  emit('update:modelValue', e.target.value); // Vue 3
};

const onBlur = e => {
  emit('blur', e.target.value);
};

const onFocus = e => {
  emit('focus', e.target.value);
};

const inputValue = computed(() => {
  const v = props.modelValue !== undefined ? props.modelValue : props.value;
  return v === null ? '' : v;
});

const componentClasses = computed(() => [
  'stk-control',
  props.iconBefore ? `has-icon-before` : '',
]);

const iconClasses = computed(() => [
  props.size ? `stk-size-${props.size}` : '',
]);

const loadingClasses = computed(() => [
  'stk-control-loader',
  props.size ? `stk-size-${props.size}` : '',
]);

const inputClasses = computed(() => [
  'stk-input',
  props.disabled ? 'is-disabled' : '',
  props.readonly ? 'is-readonly' : '',
  props.loading ? 'is-loading' : '',
  props.size ? `stk-size-${props.size}` : '',
  props.iconBefore ? `has-icon-before` : '',
  props.spacing && `spacing-${props.spacing}`,
]);
</script>

<style>
.stk-control {
  --height: calc(
    calc(var(--stk-control-line-height) * 1em) +
    calc(2 * var(--stk-control-padding-y)));
  display: flex;
  flex-grow: 1;
  position: relative;
}

.stk-control-icon {
  color: var(--stk-text-weak);
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  z-index: 1;
  height: var(--height);
  width: var(--height);
}

.stk-input {
  flex-grow: 1;

  &.is-disabled {
    --stk-control-background-color: var(--stk-background-alt);
    --stk-control-color: var(--stk-text-weak);
    pointer-events: none;
  }

  &.is-readonly {
    --stk-control-focus-outline-color: transparent;
  }

  &.has-icon-before {
    padding-left: var(--height);
  }

  &.spacing-tight {
    --stk-control-padding-x: 0.5em;
    --stk-control-padding-y: 0.375em;
  }
}

.stk-input::placeholder {
  color: var(--stk-text-weak);
}

.stk-control-loader {
  color: var(--stk-text-weak);
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  height: var(--height);
  width: var(--height);
}
</style>
